import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useAuthStore } from "../store/authStore";
import { toast } from "../components/ui/Toaster";
import { consumePostAuthRedirect } from "../utils/postAuthRedirect";

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    identifier: "", // username or email
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { login } = useAuth();
  const { isLoading, error } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check for registration success message
  useEffect(() => {
    if (location.state?.message) {
      toast.success("Registratie succesvol!", location.state.message);
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Check if user was redirected due to expired token
  useEffect(() => {
    const expired = searchParams.get('expired');
    if (expired === 'true') {
      // Clear the URL parameter to prevent showing the message again on refresh
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('expired');
      navigate({ search: newSearchParams.toString() }, { replace: true });
      
      toast.info(
        "Sessie verlopen", 
        "Je bent automatisch uitgelogd vanwege inactiviteit. Log opnieuw in om door te gaan."
      );
    }
  }, [searchParams, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.identifier.trim()) {
      newErrors.identifier = "Gebruikersnaam of email is vereist";
    }

    if (!formData.password) {
      newErrors.password = "Wachtwoord is vereist";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(formData.identifier, formData.password);
      toast.success("Welkom terug! 🎉", "Succesvol ingelogd op Solofix");
      // Return the user to the destination they originally tried to reach
      // (e.g. a shared group-join link) if one was stored; otherwise dashboard.
      const redirectTo = consumePostAuthRedirect() || "/dashboard";
      navigate(redirectTo);
    } catch (error: any) {
      console.error("Login failed:", error);
      
      const errorCode = error.response?.data?.error;
      const errorMessage = error.response?.data?.message || error.message || "Controleer je inloggegevens en probeer opnieuw";
      
      if (errorCode === "ACCOUNT_LOCKED") {
        // Show lockout message with password reset option
        toast.error(
          "Account vergrendeld 🔒", 
          errorMessage + "\n\nJe kunt je wachtwoord resetten om weer toegang te krijgen."
        );
      } else if (errorCode === "EMAIL_NOT_VERIFIED") {
        // Handle unverified email
        toast.error(
          "E-mail niet geverifieerd 📧",
          errorMessage
        );
        
        // Store email for verification resend functionality and redirect
        localStorage.setItem('pendingVerificationEmail', formData.identifier);
        navigate("/verify-email");
      } else {
        toast.error(
          "Inloggen mislukt",
          errorMessage,
        );
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="sf-eyebrow mb-3">// inloggen</div>
        <h2 className="text-2xl font-bold text-fg-bright tracking-tight">
          Welkom terug
        </h2>
        <p className="mt-2 text-sm text-fg-muted">
          Log in en ga verder waar je gebleven was
        </p>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Global error message */}
        {error && (
          <div className="rounded-field border border-neon-pink/30 bg-neon-pink/[.08] px-4 py-3 text-sm text-neon-pink-soft">
            {error}
          </div>
        )}

        {/* Username/Email field */}
        <div>
          <label
            htmlFor="identifier"
            className="block text-sm font-semibold text-fg-soft"
          >
            Gebruikersnaam of e-mail
          </label>
          <div className="mt-1.5">
            <input
              id="identifier"
              name="identifier"
              type="text"
              autoComplete="username"
              required
              value={formData.identifier}
              onChange={handleChange}
              className={`sf-input ${
                errors.identifier ? "!border-neon-pink/60" : ""
              }`}
              placeholder="Voer je gebruikersnaam of email in"
            />
            {errors.identifier && (
              <p className="mt-1 text-sm text-neon-pink-soft">{errors.identifier}</p>
            )}
          </div>
        </div>

        {/* Password field */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-semibold text-fg-soft"
          >
            Wachtwoord
          </label>
          <div className="mt-1.5 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleChange}
              className={`sf-input pr-10 ${
                errors.password ? "!border-neon-pink/60" : ""
              }`}
              placeholder="Voer je wachtwoord in"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-fg-faint" />
              ) : (
                <Eye className="h-4 w-4 text-fg-faint" />
              )}
            </button>
            {errors.password && (
              <p className="mt-1 text-sm text-neon-pink-soft">{errors.password}</p>
            )}
          </div>
        </div>

        {/* Forgot password link */}
        <div className="flex items-center justify-end">
          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="sf-link"
            >
              Wachtwoord vergeten?
            </Link>
          </div>
        </div>

        {/* Submit button */}
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="sf-btn-primary w-full py-3.5 text-base"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Inloggen...
              </>
            ) : (
              "Inloggen"
            )}
          </button>
        </div>

        {/* Terms and privacy policy links */}
        <div className="text-xs text-fg-muted text-center">
          Door in te loggen ga je akkoord met onze{" "}
          <Link
            to="/algemene-voorwaarden"
            className="sf-link"
          >
            Algemene Voorwaarden
          </Link>{" "}
          en{" "}
          <Link
            to="/privacy-statement"
            className="sf-link"
          >
            Privacybeleid
          </Link>
        </div>

        {/* Sign up link */}
        <div className="text-center">
          <p className="text-sm text-fg-muted">
            Heb je nog geen account?{" "}
            <Link
              to="/register"
              className="sf-link"
            >
              Meld je hier aan
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginPage;
